import { action, computed, observable } from 'mobx';
import { agentAccountApi } from '@link/agent/services';
import { Ota } from '@link/agent/services/types';
import { AGENT_DEFAULT_ORG } from '@link/share/constants';
import { AccountbaseStatus } from '@link/share/services/const';
import { BaseAgent } from '@link/share/stores';

const SELECTED_AGENT_ID_KEY = 'SELECTED_AGENT_ID';
const SELECTED_AGENT_ORG_ID_KEY = 'SELECTED_AGENT_ORG_ID';

export class Agent extends BaseAgent {
  @observable accessor detail: Ota = {};
  @observable accessor detailLoaded = false;
  @observable accessor selectedAgentId: string = '';
  @observable accessor selectedAgentOrgId: string = '';
  @observable accessor sessionStorageInitialized = false;

  @action
  setDetail(ota: Ota) {
    this.detail = ota;
  }

  @action
  async getDetail(otaId: string) {
    const res = await agentAccountApi.getOtaDetail({
      otaId
    });
    this.setDetail(res.ota || {});
    return res.ota;
  }

  @action
  setDetailLoaded(loaded: boolean) {
    this.detailLoaded = loaded;
  }

  @action
  setSelectedAgentOrgId(orgId: string) {
    this.selectedAgentOrgId = orgId;
    if (orgId) {
      sessionStorage.setItem(SELECTED_AGENT_ORG_ID_KEY, orgId);
    } else {
      sessionStorage.removeItem(SELECTED_AGENT_ORG_ID_KEY);
    }
  }

  @action
  getCurrentOta(otaId: string) {
    agentAccountApi
      .getOtaDetail({
        otaId
      })
      .then(res => {
        this.setDetail(res.ota || {});
        this.setDetailLoaded(true);
      });
  }

  @action
  getAllOtas() {
    agentAccountApi
      .listOtas({ status: [AccountbaseStatus.On] })
      .then(response => {
        this.setAllOtas(response.otas || []);
        this.setAllOtasLoaded(true);
      });
  }

  @action
  getOtaOrgs(otaId: string) {
    if (otaId !== this.targetOtaId) {
      this.setOtaOrgs([]);
      this.targetOtaOrgsLoaded = false;
      return agentAccountApi
        .listOtaOrgs({ otaId, status: [AccountbaseStatus.On] })
        .then(response => {
          this.setOtaOrgs(response.orgs || []);
          this.setTargetOtaOrgsLoaded(true);
        });
    }
    return Promise.resolve();
  }

  @action
  setSelectedAgentId(otaId: string) {
    this.selectedAgentId = otaId;
    if (otaId) {
      sessionStorage.setItem(SELECTED_AGENT_ID_KEY, otaId);
      this.getDetail(otaId);
    } else {
      sessionStorage.removeItem(SELECTED_AGENT_ID_KEY);
    }
  }

  @action
  async setSelectedAgentIdAndOrgId(otaId: string) {
    this.setSelectedAgentOrgId('');
    this.setSelectedAgentId(otaId);
    await this.getOtaOrgs(otaId);
    if (this.targetOtaOrgs.length > 0) {
      const defaultOrgId = this.targetOtaOrgs.find(
        org => org.parentOtaOrgId === AGENT_DEFAULT_ORG
      )?.otaOrgId;
      this.setSelectedAgentOrgId(defaultOrgId || '');
    }
  }

  @action
  clearSelectedAgentInfo() {
    this.setSelectedAgentId('');
    this.setSelectedAgentOrgId('');
    this.sessionStorageInitialized = false;
    this.setDetail({} as Ota);
    this.setDetailLoaded(false);
    this.setOtaOrgs([]);
    this.setTargetOtaOrgsLoaded(false);
  }

  @action
  async initFromSessionStorage() {
    if (this.sessionStorageInitialized) return;
    this.sessionStorageInitialized = true;
    // @action only covers sync code before the first await; mutations after awaits use @action methods.
    const savedAgentId = sessionStorage.getItem(SELECTED_AGENT_ID_KEY);
    const savedOrgId = sessionStorage.getItem(SELECTED_AGENT_ORG_ID_KEY);

    if (savedAgentId) {
      const isValidAgentId = this.allOtas.some(
        ota => ota.otaId === savedAgentId
      );

      if (isValidAgentId) {
        this.selectedAgentId = savedAgentId;
        this.getDetail(savedAgentId);
        await this.getOtaOrgs(savedAgentId);

        // Ensure a valid org ID is set, preventing soft-lock
        let targetOrgId = '';
        if (
          savedOrgId &&
          this.targetOtaOrgs.some(org => org.otaOrgId === savedOrgId)
        ) {
          targetOrgId = savedOrgId;
        } else {
          targetOrgId =
            this.defaultParentOrgId || this.targetOtaOrgs[0]?.otaOrgId || '';
        }

        if (targetOrgId) {
          this.setSelectedAgentOrgId(targetOrgId);
        }

        return;
      }
    }

    // No valid saved agent, fall back to first agent
    if (this.allOtas.length > 0) {
      const firstOtaId = this.allOtas[0]?.otaId || '';
      if (firstOtaId) {
        await this.setSelectedAgentIdAndOrgId(firstOtaId);
      }
    }
  }

  @computed
  get hasSubOrg() {
    return this.targetOtaOrgs.length > 1;
  }

  @computed
  get paymentType() {
    return this.detail?.paymentType;
  }
}

export default new Agent();
